import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectorRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../services/api.service';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import {
  createModalLogoUploadConfig,
  getDisplayNameForLogo,
  handleModalLogoUpload,
  removeModalLogo,
  revokeBlobUrl,
} from '../campaign-logo-upload.util';
import { resolveLandingPageViewUrl } from '../campaign-platform-url.util';
import {
  buildConsentTextSegments,
  ConsentTextSegment,
  buildDefaultConsentText,
  syncConsentLabelInText,
} from '../consent-link.util';

export interface LandingPageModalData {
  logo?: string;
  title: string;
  subtitle: string;
  inputLabel: string;
  inputType: string;
  buttonText: string;
  buttonColor: string;
  consentText: string;
  consentLabel: string;
  consentUrl: string;
  contestText?: string;
  validateEmail: boolean;
  validateEmailErrorMessage: string;
  validatePhone: boolean;
  validatePhoneErrorMessage: string;
  validateDigits: boolean;
  digitLength: number | null;
  validateDigitsErrorMessage: string;
  showConsentBlock: boolean;
}

/** Default copy shown as input placeholders; subtitle/input label still default on save when empty. */
export const LANDING_PAGE_PLACEHOLDERS: LandingPageModalData = {
  title: 'Get 20% off your first wash',
  subtitle: 'Limited Black Friday offer',
  inputLabel: 'Phone number',
  inputType: 'Phone',
  buttonText: 'Claim my offer',
  buttonColor: '#2F6DDE',
  consentText: buildDefaultConsentText('terms & conditions'),
  consentLabel: 'terms & conditions',
  consentUrl: 'https://way.com/terms',
  validateEmail: true,
  validateEmailErrorMessage: 'Please enter a valid email address',
  validatePhone: true,
  validatePhoneErrorMessage: 'Please enter a valid US phone number',
  validateDigits: true,
  digitLength: 10,
  validateDigitsErrorMessage: 'Please enter a valid 10-digit US number',
  showConsentBlock: true,
};

export const DEFAULT_LANDING_PAGE_DATA: LandingPageModalData = { ...LANDING_PAGE_PLACEHOLDERS };

const DEFAULT_LANDING_VALIDATION_ERRORS = {
  logo: '',
  title: '',
  buttonText: '',
  validateEmailErrorMessage: '',
  validatePhoneErrorMessage: '',
  digitLength: '',
  validateDigitsErrorMessage: '',
};

const EMPTY_FORM_DATA: LandingPageModalData = {
  title: '',
  subtitle: '',
  inputLabel: '',
  inputType: 'Phone',
  buttonText: '',
  buttonColor: LANDING_PAGE_PLACEHOLDERS.buttonColor,
  consentText: '',
  consentLabel: '',
  consentUrl: '',
  validateEmail: true,
  validateEmailErrorMessage: '',
  validatePhone: true,
  validatePhoneErrorMessage: '',
  validateDigits: true,
  digitLength: null,
  validateDigitsErrorMessage: '',
  showConsentBlock: true,
};

@Component({
  selector: 'app-create-modal-landing-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './create-modal-landing-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-modal-landing-page.component.scss',
})
export class CreateModalLandingPageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() initialData: LandingPageModalData | null = null;
  @Input() campaignPlatforms: any = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<LandingPageModalData>();

  formData: LandingPageModalData = { ...EMPTY_FORM_DATA };
  readonly placeholders = LANDING_PAGE_PLACEHOLDERS;
  previewMode: 'desktop' | 'mobile' = 'desktop';
  logoFile: File | null = null;
  logoBlobUrl = '';
  logoFileName = '';
  isUploadingLogo = false;
  fileValidationError = '';
  validationErrors = { ...DEFAULT_LANDING_VALIDATION_ERRORS };
  readonly maxLogoDimensions = { width: 1000, height: 1000 };
  readonly maxLogoFileSizeBytes = 10 * 1024 * 1024;
  readonly buttonColors = [
    '#2F6DDE',
    '#15172E',
    '#22C55E',
    '#EAB308',
    '#06B6D4',
    '#EC4899',
    '#4F46E5',
    '#14B8A6',
    '#F97316',
    '#A855F7',
    '#8B5CF6',
    '#7C3AED',
  ];

  private apiService = inject(ApiService);
  private messageService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private previousConsentLabel = '';

  ngOnInit(): void {
    this.populateFormFields();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['isOpen'] && this.isOpen) || changes['initialData']) {
      this.populateFormFields();
    }
  }

  populateFormFields(): void {
    if (
      this.initialData &&
      (this.initialData.title?.trim() || this.initialData.buttonText?.trim())
    ) {
      this.formData = { ...this.initialData };
      if (this.initialData.logo) {
        this.logoFileName = getDisplayNameForLogo(this.initialData.logo);
      }
      // Skip onInputTypeChange() — it would override API-provided toggle values with hardcoded defaults.
    } else {
      this.formData = { ...EMPTY_FORM_DATA };
      this.clearLogo();
      this.onInputTypeChange();
    }
    this.previewMode = 'desktop';
    this.clearValidationErrors();
    this.fileValidationError = '';
    this.previousConsentLabel = this.formData.consentLabel?.trim() || '';
  }

  ngOnDestroy(): void {
    this.revokeLogoBlobUrl();
  }

  get logoPreviewUrl(): string {
    return this.logoBlobUrl || this.formData.logo || '';
  }

  onClose(): void {
    this.revokeLogoBlobUrl();
    this.close.emit();
  }

  onReset(): void {
    this.formData = { ...EMPTY_FORM_DATA };
    this.clearValidationErrors();
    this.clearLogo();
    this.onInputTypeChange(); //validate toggle buttons
  }

  onSave(): void {
    if (!this.validateForm()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Complete landing page details!',
        detail: 'Fill in the empty fields to save.',
      });
      return;
    }
    this.applyOptionalPlaceholderDefaults();
    const savedLogoUrl = this.formData.logo?.trim() || '';
    this.save.emit({
      ...this.formData,
      logo: savedLogoUrl,
      consentText: this.formData.consentText?.trim() || '',
      consentLabel: this.formData.consentLabel?.trim() || '',
      consentUrl: this.formData.consentUrl?.trim() || '',
    });
    this.releaseLocalLogoPreview();
  }

  validateForm(): boolean {
    this.clearValidationErrors();

    let isValid = true;

    if (!this.formData.logo?.trim() && !this.logoBlobUrl) {
      this.validationErrors.logo = 'Logo is required';
      isValid = false;
    }

    if (!this.formData.title?.trim()) {
      this.validationErrors.title = 'Title is required';
      isValid = false;
    }

    if (!this.formData.buttonText?.trim()) {
      this.validationErrors.buttonText = 'Button text is required';
      isValid = false;
    }

    if (this.isUploadingLogo) {
      isValid = false;
    }

    if (this.isEmailInputType) {
      if (this.formData.validateEmail && !this.formData.validateEmailErrorMessage?.trim()) {
        this.validationErrors.validateEmailErrorMessage = 'Error message is required';
        isValid = false;
      }
    } else {
      if (this.formData.validatePhone && !this.formData.validatePhoneErrorMessage?.trim()) {
        this.validationErrors.validatePhoneErrorMessage = 'Error message is required';
        isValid = false;
      }
      if (this.formData.validateDigits) {
        if (!Number(this.formData.digitLength) || Number(this.formData.digitLength) <= 0) {
          this.validationErrors.digitLength = 'Number of digits is required';
          isValid = false;
        }
        if (!this.formData.validateDigitsErrorMessage?.trim()) {
          this.validationErrors.validateDigitsErrorMessage = 'Error message is required';
          isValid = false;
        }
      }
    }

    return isValid;
  }

  clearValidationErrors(): void {
    this.validationErrors = { ...DEFAULT_LANDING_VALIDATION_ERRORS };
  }

  /** Fills empty optional non-consent fields from placeholders before save. */
  private applyOptionalPlaceholderDefaults(): void {
    const p = LANDING_PAGE_PLACEHOLDERS;
    if (!this.formData.subtitle?.trim()) {
      this.formData.subtitle = p.subtitle;
    }
    if (!this.formData.inputLabel?.trim()) {
      this.formData.inputLabel = this.isEmailInputType ? 'Email' : p.inputLabel;
    }
  }

  private getFormDataWithPlaceholders(): LandingPageModalData {
    const p = LANDING_PAGE_PLACEHOLDERS;
    return {
      ...this.formData,
      title: this.formData.title?.trim() || p.title,
      subtitle: this.formData.subtitle?.trim() || p.subtitle,
      buttonText: this.formData.buttonText?.trim() || p.buttonText,
      inputLabel:
        this.formData.inputLabel?.trim() || (this.isEmailInputType ? 'Email' : p.inputLabel),
      consentText:
        this.formData.consentText?.trim() ||
        buildDefaultConsentText(this.formData.consentLabel?.trim() || p.consentLabel),
      consentLabel: this.formData.consentLabel?.trim() || p.consentLabel,
      consentUrl: this.formData.consentUrl?.trim() || p.consentUrl,
      validateEmailErrorMessage: this.formData.validateEmailErrorMessage?.trim() || '',
      validatePhoneErrorMessage: this.formData.validatePhoneErrorMessage?.trim() || '',
      validateDigitsErrorMessage: this.formData.validateDigitsErrorMessage?.trim() || '',
      digitLength: Number(this.formData.digitLength) || 0,
    };
  }

  onTitleChange(): void {
    if (this.validationErrors.title && this.formData.title?.trim()) {
      this.validationErrors.title = '';
    }
  }

  onButtonTextChange(): void {
    if (this.validationErrors.buttonText && this.formData.buttonText?.trim()) {
      this.validationErrors.buttonText = '';
    }
  }

  onValidateEmailErrorMessageChange(): void {
    if (
      this.validationErrors.validateEmailErrorMessage &&
      this.formData.validateEmailErrorMessage?.trim()
    ) {
      this.validationErrors.validateEmailErrorMessage = '';
    }
  }

  onValidatePhoneErrorMessageChange(): void {
    if (
      this.validationErrors.validatePhoneErrorMessage &&
      this.formData.validatePhoneErrorMessage?.trim()
    ) {
      this.validationErrors.validatePhoneErrorMessage = '';
    }
  }

  onDigitLengthChange(): void {
    if (this.validationErrors.digitLength && Number(this.formData.digitLength) > 0) {
      this.validationErrors.digitLength = '';
    }
  }

  onValidateDigitsErrorMessageChange(): void {
    if (
      this.validationErrors.validateDigitsErrorMessage &&
      this.formData.validateDigitsErrorMessage?.trim()
    ) {
      this.validationErrors.validateDigitsErrorMessage = '';
    }
  }

  onColorSelect(color: string): void {
    this.formData.buttonColor = color;
  }

  get isEmailInputType(): boolean {
    return (this.formData.inputType || '').toLowerCase() === 'email';
  }

  /** True when landing page content was loaded from the campaign (API), not a new create flow. */
  get showViewInBrowserLink(): boolean {
    return Boolean(this.initialData?.title?.trim() || this.initialData?.buttonText?.trim());
  }

  getPreviewInputPlaceholder(): string {
    return this.isEmailInputType ? 'example@gmail.com' : 'e.g. 555-555-5555';
  }

  onInputTypeChange(): void {
    if (this.isEmailInputType) {
      this.formData.validatePhone = false;
      this.formData.validateDigits = false;
      this.formData.validateEmail = true;
      return;
    }

    this.formData.validateEmail = false;
    this.formData.validatePhone = true;
    this.formData.validateDigits = true;
  }

  setPreviewMode(mode: 'desktop' | 'mobile'): void {
    this.previewMode = mode;
  }

  onViewInBrowser(): void {
    const url = resolveLandingPageViewUrl(this.campaignPlatforms);
    if (!url) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Landing page unavailable',
        detail: 'Landing page URL is not available yet. Save the campaign and check UTM tagging.',
      });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  onConsentLabelChange(nextLabel: string): void {
    if (this.formData.consentText?.trim()) {
      this.formData.consentText = syncConsentLabelInText(
        this.formData.consentText,
        this.previousConsentLabel,
        nextLabel
      );
    }
    this.previousConsentLabel = nextLabel.trim();
  }

  getConsentPreviewSegments(): ConsentTextSegment[] {
    const resolved = this.getFormDataWithPlaceholders();
    return buildConsentTextSegments(
      resolved.consentText,
      resolved.consentLabel,
      resolved.consentUrl
    );
  }

  async onLogoUpload(event: Event): Promise<void> {
    await handleModalLogoUpload(event, this.buildLogoUploadConfig(), formData =>
      this.apiService.uploadCampaignFile(formData)
    );
  }

  removeLogo(event?: Event): void {
    removeModalLogo(event, () => this.clearLogo());
  }

  private buildLogoUploadConfig() {
    return createModalLogoUploadConfig({
      maxWidth: this.maxLogoDimensions.width,
      maxHeight: this.maxLogoDimensions.height,
      maxBytes: this.maxLogoFileSizeBytes,
      revokeBlob: () => this.revokeLogoBlobUrl(),
      assignPreparedFile: (file, blobUrl, fileName) => {
        this.logoFile = file;
        this.logoBlobUrl = blobUrl;
        this.logoFileName = fileName;
        this.formData.logo = '';
        this.cdr.detectChanges();
      },
      applyUploadedLogo: logoUrl => {
        this.formData.logo = logoUrl;
        this.logoFileName = getDisplayNameForLogo(logoUrl);
        this.validationErrors.logo = '';
        this.cdr.detectChanges();
      },
      onUploadFailure: () => this.clearLogoStateAfterError(),
      setUploading: uploading => {
        this.isUploadingLogo = uploading;
      },
      setValidationError: message => {
        this.fileValidationError = message;
      },
      detectChanges: () => this.cdr.detectChanges(),
      showUploadErrorToast: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Upload failed',
          detail: 'Failed to upload logo. Please try again.',
        });
      },
    });
  }

  private clearLogoStateAfterError(): void {
    this.logoFile = null;
    this.logoFileName = '';
    this.formData.logo = '';
    this.releaseLocalLogoPreview();
  }

  private releaseLocalLogoPreview(): void {
    this.logoFile = null;
    this.revokeLogoBlobUrl();
  }

  private clearLogo(): void {
    this.logoFile = null;
    this.formData.logo = '';
    this.logoFileName = '';
    this.fileValidationError = '';
    this.releaseLocalLogoPreview();
  }

  private revokeLogoBlobUrl(): void {
    revokeBlobUrl(this.logoBlobUrl);
    this.logoBlobUrl = '';
  }
}
